<script setup lang="ts">
import { inject, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { DarkModeProvider, darkModeProviderKey } from '@/utils/theme.util'
import { Sun, Moon, Monitor, Palette, Ghost, Trash2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { useProfilesStore } from '@/store/modules/userProfile'
import HiddenTemplatesPreferences from './HiddenTemplatesPreferences.vue'

const route = useRoute()
const isDirect = computed(() => {
  const path = route?.path || ''
  return path.startsWith('/bet') && !path.startsWith('/betpic')
})

const profilesStore = useProfilesStore()
const hiddenPrefsRef = ref<InstanceType<typeof HiddenTemplatesPreferences> | null>(null)

const { colorMode } = inject<DarkModeProvider>(darkModeProviderKey) as DarkModeProvider

function setMode(mode: 'light' | 'dark' | 'auto') {
  colorMode.value = mode
}
</script>

<template>
  <div class="space-y-8 pb-8">
    <section>
      <div class="mb-5">
        <div class="flex items-center gap-2">
          <Palette class="w-5 h-5 text-gray-400" />
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">Theme & Appearance</h3>
        </div>
        <p class="text-sm text-gray-500 mt-0.5 ml-7">Customise the interface visual styles and colour mode</p>
      </div>

      <div class="overflow-hidden rounded-xl border border-border dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 md:p-6 space-y-6">
        <div class="flex flex-col gap-3">
          <label class="text-sm font-bold text-gray-900 dark:text-white tracking-tight">Colour Mode</label>
          <p class="text-xs text-gray-500 mb-2">Select a light or dark aesthetic, or sync with your system automatically.</p>
          
          <div class="grid grid-cols-3 gap-2 p-1 bg-gray-100 dark:bg-gray-800/50 rounded-xl relative">
            <button
              @click="setMode('light')"
              class="flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg text-sm font-medium transition-all"
              :class="colorMode === 'light' ? 'bg-white dark:bg-gray-700 text-primary shadow-sm border border-gray-200/50 dark:border-gray-600/50 scale-100' : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 scale-95 border border-transparent'"
            >
              <Sun class="w-5 h-5 mb-1" />
              Light
            </button>
            <button
              @click="setMode('auto')"
              class="flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg text-sm font-medium transition-all"
              :class="colorMode === 'auto' ? 'bg-white dark:bg-gray-700 text-primary shadow-sm border border-gray-200/50 dark:border-gray-600/50 scale-100' : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 scale-95 border border-transparent'"
            >
              <Monitor class="w-5 h-5 mb-1" />
              System
            </button>
            <button
              @click="setMode('dark')"
              class="flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg text-sm font-medium transition-all"
              :class="colorMode === 'dark' ? 'bg-white dark:bg-gray-700 text-primary shadow-sm border border-gray-200/50 dark:border-gray-600/50 scale-100' : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 scale-95 border border-transparent'"
            >
              <Moon class="w-5 h-5 mb-1" />
              Dark
            </button>
          </div>
        </div>
      </div>
    </section>
    
    <section v-if="!isDirect" id="hidden-templates">
      <div class="mb-5 flex items-center justify-between gap-4">
        <div>
          <div class="flex items-center gap-2">
            <Ghost class="w-5 h-5 text-gray-400" />
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">Hidden Templates</h3>
          </div>
          <p class="text-sm text-gray-500 mt-0.5 ml-7">Manage your hidden templates. Click on the eye to unhide</p>
        </div>

        <Button 
          v-if="profilesStore.profile.hiddenTemplates?.length"
          variant="outline" 
          size="sm" 
          class="shrink-0 px-3 text-xs h-8 rounded-lg border-red-200 text-red-500 hover:bg-red-50 dark:border-red-900/30 dark:hover:bg-red-900/10"
          @click="hiddenPrefsRef?.clearAll()"
        >
          <Trash2 class="w-3 h-3 mr-2" />
          Unhide All
        </Button>
      </div>

      <div class="overflow-hidden rounded-xl border border-border dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 md:p-6">
        <HiddenTemplatesPreferences ref="hiddenPrefsRef" />
      </div>
    </section>
  </div>
</template>
